import React from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';

import CustomCheckBox from '../CustomCheckBox';

export const DEFAULT_WORKLIST_COLUMNS = [
  'patientName',
  'accession',
  'institutionName',
  'studyDate',
  'description',
  'modalities',
  'jobStatus',
  'instances',
];

export const MANDATORY_WORKLIST_COLUMNS = [
  'patientName',
  'studyDate',
  'jobStatus',
  'instances',
];

export const WORKLIST_COLUMNS_KEY = 'worklist_columns';

const WorklistColumns = ({
  state,
  defaultColumnNames,
  handleToggleColumn,
  handleSelectAllColumns,
  allColumns = [],
  mandatoryColumnNames = MANDATORY_WORKLIST_COLUMNS,
}) => {
  const { t } = useTranslation('UserPreferencesModal');

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-slate-400">
          {t('Select visible columns')}
        </span>
        {state.visibleColumns.length < defaultColumnNames.length && (
          <button
            type="button"
            onClick={handleSelectAllColumns}
            className="text-xs font-semibold text-indigo-400 hover:text-indigo-300 transition-colors"
          >
            {t('Select all')}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 max-h-56 overflow-y-auto ohif-scrollbar p-1">
        {allColumns.map(col => {
          const isMandatory = mandatoryColumnNames.includes(col.name);
          const isChecked = isMandatory || state.visibleColumns.includes(col.name);

          return (
            <div
              key={col.name}
              role={isMandatory ? undefined : 'button'}
              tabIndex={isMandatory ? -1 : 0}
              onClick={() => {
                if (!isMandatory) {
                  handleToggleColumn(col.name);
                }
              }}
              onKeyDown={e => {
                if (!isMandatory && (e.key === ' ' || e.key === 'Enter')) {
                  e.preventDefault();
                  handleToggleColumn(col.name);
                }
              }}
              className={classnames(
                'flex items-center justify-between p-2.5 rounded-xl border transition-all select-none',
                isMandatory
                  ? 'bg-indigo-900/40 border-indigo-700/30 text-indigo-400 cursor-not-allowed opacity-90'
                  : isChecked
                  ? 'bg-indigo-900/40 border-indigo-700/30 text-indigo-400 cursor-pointer focus:outline-none focus:ring-1 focus:ring-indigo-500 hover:border-indigo-600/50 hover:bg-indigo-800/30'
                  : 'bg-indigo-950/40 border-indigo-500/40 text-slate-100 shadow-sm cursor-pointer focus:outline-none focus:ring-1 focus:ring-indigo-500 hover:border-indigo-600/50 hover:bg-indigo-800/30'
              )}
            >
              <div className="flex items-center gap-3 truncate mr-2">
                <CustomCheckBox
                  checked={isChecked}
                  disabled={isMandatory}
                  onChange={() => {
                    if (!isMandatory) {
                      handleToggleColumn(col.name);
                    }
                  }}
                />
                <span className="text-xs font-semibold truncate">{col.displayName || col.name}</span>
              </div>
              {isMandatory && (
                <span className="text-[10px] font-semibold uppercase px-2 py-0.5 rounded-md bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 flex-shrink-0">
                  {t('Required')}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default WorklistColumns;
